import Icon from '../../components/Icon';
import { Banner, Select, TextArea, TextInput, Toggle } from '../../components/ui';
import { HAS_OPTIONS, TYPE_LABEL, keyFromLabel, uniqueKey } from './fieldMeta';
import type { FormKind } from './formKinds';
import type { FieldOption, FieldType, FormField } from '../../types';

interface Props {
  /** What kind of form this is — see formKinds.ts. */
  kind: FormKind;
  field: FormField;
  /** Keys already in a published version. Their key and type are frozen. */
  publishedKeys: Set<string>;
  publishedType: FieldType | undefined;
  takenKeys: Set<string>;
  onChange: (next: FormField) => void;
}

export default function FieldInspector({
  kind, field, publishedKeys, publishedType, takenKeys, onChange,
}: Props) {
  const isPublished = publishedKeys.has(field.key);

  /**
   * A new field's key follows its label, so the admin never has to think
   * about keys — until they type one themselves, at which point the key
   * no longer matches what the label would produce and the sync stops on
   * its own. Deriving this rather than remembering a "user edited the
   * key" flag means there is no stale flag to reset when the selection
   * changes. After publishing, the key is what existing values are filed
   * under, so it follows nothing.
   */
  const keyFollowsLabel = !isPublished
    && !field.system
    && field.key === keyFromLabel(field.label || 'field');

  const patch = (changes: Partial<FormField>) => onChange({ ...field, ...changes });

  function onLabelChange(label: string) {
    if (!keyFollowsLabel) {
      patch({ label });
      return;
    }
    const others = new Set(takenKeys);
    others.delete(field.key);
    patch({ label, key: uniqueKey(keyFromLabel(label || 'field'), others) });
  }

  function setOption(index: number, changes: Partial<FieldOption>) {
    const options = [...(field.options ?? [])];
    const current = options[index];
    if (!current) return;
    options[index] = { ...current, ...changes };
    patch({ options });
  }

  function addOption() {
    const options = [...(field.options ?? [])];
    const n = options.length + 1;
    options.push({ value: `option_${n}`, label: `Option ${n}` });
    patch({ options });
  }

  function removeOption(index: number) {
    patch({ options: (field.options ?? []).filter((_, i) => i !== index) });
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{field.system ? 'Built-in field' : 'Field settings'}</h3>
        <span className="badge badge-neutral">{TYPE_LABEL[field.type]}</span>
      </div>

      <div className="card-pad col gap-16">
        {field.system && kind.systemFieldNote ? (
          <Banner kind="info">
            {kind.systemFieldNote} You can rename it and move it, but not remove it.
          </Banner>
        ) : null}

        <TextInput
          label="Label"
          data-testid="field-label"
          value={field.label}
          maxLength={80}
          placeholder="Owner Name"
          hint="What the rep reads above the box"
          onChange={(e) => onLabelChange(e.target.value)}
        />

        {!field.system ? (
          <TextInput
            label="Field key"
            data-testid="field-key"
            value={field.key}
            disabled={isPublished}
            maxLength={40}
            hint={isPublished
              ? 'Locked — this is what existing values are stored under'
              : 'Used in exports and reports. Lowercase, no spaces.'}
            onChange={(e) => patch({
              key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
            })}
          />
        ) : null}

        {isPublished && publishedType ? (
          <Banner kind="warning">
            This field is already collecting {TYPE_LABEL[publishedType].toLowerCase()} values,
            so its type cannot be changed. To collect something else, add a new field.
          </Banner>
        ) : null}

        {!field.system && !isPublished ? (
          <Select
            label="Type"
            value={field.type}
            onChange={(e) => {
              const type = e.target.value as FieldType;
              patch({
                type,
                options: HAS_OPTIONS(type)
                  ? field.options ?? [{ value: 'option_1', label: 'Option 1' }]
                  : undefined,
              });
            }}
          >
            {(Object.keys(TYPE_LABEL) as FieldType[])
              .filter((t) => t !== 'route')
              .map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </Select>
        ) : null}

        <TextInput
          label="Helper text"
          value={field.helpText ?? ''}
          maxLength={200}
          placeholder="Optional"
          hint="A short line under the box, if the label needs explaining"
          onChange={(e) => patch({ helpText: e.target.value || undefined })}
        />

        {['text', 'textarea', 'number', 'phone', 'email'].includes(field.type) ? (
          <TextInput
            label="Placeholder"
            value={field.placeholder ?? ''}
            maxLength={80}
            placeholder="Optional example"
            onChange={(e) => patch({ placeholder: e.target.value || undefined })}
          />
        ) : null}

        {field.type === 'number' ? (
          <div className="row gap-12">
            <TextInput
              label="Minimum"
              type="number"
              value={field.min ?? ''}
              onChange={(e) => patch({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
            <TextInput
              label="Maximum"
              type="number"
              value={field.max ?? ''}
              onChange={(e) => patch({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          </div>
        ) : null}

        {field.type === 'text' || field.type === 'textarea' ? (
          <TextInput
            label="Character limit"
            type="number"
            min={1}
            max={4000}
            value={field.maxLength ?? ''}
            placeholder={field.type === 'textarea' ? '2000' : '200'}
            onChange={(e) => patch({
              maxLength: e.target.value === '' ? undefined : Number(e.target.value),
            })}
          />
        ) : null}

        {HAS_OPTIONS(field.type) ? (
          <div className="field">
            <label>Options</label>
            {(field.options ?? []).map((option, index) => (
              <div className="option-row" key={index}>
                <input
                  className="input"
                  value={option.label}
                  placeholder="What the rep sees"
                  onChange={(e) => {
                    const label = e.target.value;
                    // The stored value follows the label only until it has
                    // been saved with data behind it; keeping them in step
                    // is what makes an export readable.
                    setOption(index, {
                      label,
                      value: isPublished ? option.value : keyFromLabel(label) || option.value,
                    });
                  }}
                />
                <button
                  type="button"
                  className="icon-btn danger"
                  aria-label="Remove option"
                  onClick={() => removeOption(index)}
                >
                  <Icon name="x" size={15} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>
              <Icon name="plus" size={15} /> Add option
            </button>
          </div>
        ) : null}

        {field.type === 'textarea' ? (
          <TextArea
            label="Default text"
            value={field.placeholder ?? ''}
            placeholder="Optional"
            onChange={(e) => patch({ placeholder: e.target.value || undefined })}
          />
        ) : null}

        <div className="col gap-12" style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <Toggle
            testId="field-required"
            checked={Boolean(field.required)}
            disabled={field.system}
            label="Required — the rep cannot save without it"
            onChange={(required) => patch({ required })}
          />
          <Toggle
            testId="field-show-in-list"
            checked={Boolean(field.showInList)}
            label={kind.listLabel}
            onChange={(showInList) => patch({ showInList })}
          />
        </div>
      </div>
    </div>
  );
}
